import { Switch } from '@headlessui/react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FormEventHandler, HTMLAttributes, InputHTMLAttributes, useState } from 'react'

import { useDependencies } from '@/components/providers/DependencyProvider'
import { useId } from '@/hooks/useId'
import { Status, useStatus } from '@/hooks/useStatus'

function FormInput({ className, label, type, required, value, isOptional, onChange, autoComplete, minLength, maxLength }: {
	className?: HTMLAttributes<HTMLDivElement>['className'],
	label: string,
	isOptional?: true,
	type: InputHTMLAttributes<HTMLInputElement>['type'],
	required?: InputHTMLAttributes<HTMLInputElement>['required'],
	value: InputHTMLAttributes<HTMLInputElement>['value'],
	onChange: InputHTMLAttributes<HTMLInputElement>['onChange'],
	autoComplete: InputHTMLAttributes<HTMLInputElement>['autoComplete'],
	minLength?: InputHTMLAttributes<HTMLInputElement>['minLength'],
	maxLength?: InputHTMLAttributes<HTMLInputElement>['maxLength'],
}) {
	const id = useId()

	return (
		<div className={className}>
			<div className="flex justify-between text-sm md:text-base">
				<label htmlFor={id}>{label}</label>
				{(isOptional ?? false) && <p className="text-gray-400">(optional)</p>}
			</div>
			<input
				className="mt-1.5 px-2 py-1.5 text-sm shadow-input appearance-none w-full h-8 rounded md:h-11 md:rounded-lg"
				type={type}
				id={id}
				required={required}
				value={value}
				onChange={onChange}
				autoComplete={autoComplete}
				minLength={minLength}
				maxLength={maxLength}
			/>
		</div>
	)
}

export default function RegisterForm({ isDealer, title }: { isDealer?: boolean, title: string }): JSX.Element {
	const { apiFetcher } = useDependencies()
	const [state, setState] = useState({
		first: '',
		last: '',
		phone: '',
		email: '',
		company: '',
		password: '',
		passwordConfirm: '',
		address: {
			addr1: '',
			addr2: '',
			country: 'US',
			city: '',
			state: '',
			zip: '',
		},
		newsletter: false,
	})

	const [status, setStatus] = useStatus(Status.Idle)
	const [reasons, setReasons] = useState<Array<JSX.Element>>([])
	const router = useRouter()
	const previousURL = Array.isArray(router.query.back) ? router.query.back[0] : router.query.back

	const handleRegister: FormEventHandler = event => {
		event.preventDefault()

		if (status === Status.Success || status === Status.Loading) {
			return
		}

		setReasons([])
		const reasons: Array<JSX.Element> = []

		if (state.password !== state.passwordConfirm) {
			reasons.push((
				<p key="unmatched-passwords">Passwords do not match.</p>
			))
		}

		if (state.password.length < 8) {
			reasons.push((
				<p key="password-length">Passwords must be at least eight characters.</p>
			))
		}

		if (state.password.search(/[a-zA-Z]+/gu) === -1 || state.password.search(/[0-9]+/gu) === -1) {
			reasons.push((
				<p key="alphanumeric-password">Passwords must contain both alphabetic and numeric characters.</p>
			))
		}

		if (reasons.length > 0) {
			setReasons(reasons)
			return
		}

		const body = { ...state, ...(isDealer === true ? { dealer: true } : {}) }

		setStatus(Status.Loading)
		apiFetcher<{
			success: true,
		} | {
			success: false,
			error: string,
		}>('/register', {
			method: 'POST',
			body: JSON.stringify(body),
		}).then(response => {
			if (response.success) {
				setStatus(Status.Success)
				return
			}

			if (response.error === 'user already exists') {
				setReasons([<p className="text-gray-800" key="user-already-exists">User already exists. <Link href="/login"><a className="underline text-green-400 text-sm font-semibold md:text-base">Login</a></Link></p>])
			}
		}).catch(() => {
			setStatus(Status.Error)
		})
	}

	const loginUrl = `/login${previousURL === undefined ? '' : `?back=${previousURL}`}`

	return (
		<form onSubmit={handleRegister}>
			{ status === Status.Success ? (
				<>
					<h1 className="text-xl text-center font-bold md:text-3xl">Account Created</h1>

					<div className="mt-4 text-center space-y-6">
						<p className="">An email has been sent to you in order to verify your email address.</p>

						<Link href={loginUrl}><a className="block mx-auto mt-6 py-2 w-full bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase text-base font-extrabold leading-5 md:mt-8 md:h-11 md:max-w-xs md:text-lg">Login</a></Link>

						<Link href="/"><a className="inline-block underline text-gray-800 text-sm font-semibold md:text-base">Return to home</a></Link>
					</div>
				</>
			) : (
				<>
					<h2 className="text-xl font-bold text-center md:text-3xl">{ title }</h2>

					<div className="grid grid-cols-6 mt-6 gap-y-3 md:mt-12 md:gap-x-8 md:gap-y-8">
						<FormInput
							className="col-span-6 md:col-span-2"
							label="First Name"
							type="text"
							required
							value={state.first}
							onChange={e => setState({ ...state, first: e.target.value })}
							autoComplete="given-name"
						/>

						<FormInput
							className="col-span-6 md:col-span-2"
							label="Last Name"
							type="text"
							required
							value={state.last}
							onChange={e => setState({ ...state, last: e.target.value })}
							autoComplete="family-name"
						/>

						<FormInput
							className="col-span-6 md:col-span-2"
							label="Phone Number"
							isOptional
							maxLength={50}
							type="text"
							required
							value={state.phone}
							onChange={e => setState({ ...state, phone: e.target.value })}
							autoComplete="tel"
						/>

						<FormInput
							className="col-span-6 md:col-span-3"
							label="Email"
							type="email"
							required
							value={state.email}
							onChange={e => setState({ ...state, email: e.target.value })}
							autoComplete="email"
						/>

						<FormInput
							className="col-span-6 md:col-span-3"
							label="Company Name"
							isOptional
							type="text"
							value={state.company}
							onChange={e => setState({ ...state, company: e.target.value })}
							autoComplete="forganization"
						/>

						<FormInput
							className="col-span-6 md:col-span-3"
							label="Password"
							type="password"
							required
							value={state.password}
							onChange={e => setState({ ...state, password: e.target.value })}
							autoComplete="new-password"
							minLength={8}
						/>

						<FormInput
							className="col-span-6 md:col-span-3"
							label="Confirm Password"
							type="password"
							required
							value={state.passwordConfirm}
							onChange={e => setState({ ...state, passwordConfirm: e.target.value })}
							autoComplete="off"
						/>

						<FormInput
							className="col-span-6"
							label="Address Line 1"
							type="text"
							required
							value={state.address.addr1}
							onChange={e => setState({ ...state, address: { ...state.address, addr1: e.target.value }})}
							autoComplete="address-line1"
						/>

						<FormInput
							className="col-span-6 md:col-span-3"
							label="Address Line 2"
							isOptional
							type="text"
							value={state.address.addr2}
							onChange={e => setState({ ...state, address: { ...state.address, addr2: e.target.value }})}
							autoComplete="address-line2"
						/>

						<div className="col-span-6 md:col-span-3">
							<div className="flex justify-between text-sm md:text-base">
								<label htmlFor="country">Country</label>
							</div>
							<select
								className="mt-1.5 px-2 py-1.5 text-sm shadow-input w-full h-8 rounded md:h-11 md:rounded-lg"
								id="country"
								required
								defaultValue={state.address.country}
								onChange={e => setState({ ...state, address: { ...state.address, country: e.target.value }})}
								onBlur={e => setState({ ...state, address: { ...state.address, country: e.target.value }})}
								autoComplete="country"
							>
								<option value="US">United States</option>
							</select>
						</div>

						<FormInput
							className="col-span-6 md:col-span-2"
							label="City"
							type="text"
							required
							value={state.address.city}
							onChange={e => setState({ ...state, address: { ...state.address, city: e.target.value }})}
							autoComplete="address-level2"
						/>

						<div className="col-span-6 md:col-span-2">
							<div className="flex justify-between text-sm md:text-base">
								<label htmlFor="state">State</label>
							</div>
							<select
								className="mt-1.5 px-2 py-1.5 text-sm shadow-input w-full h-8 rounded md:h-11 md:rounded-lg"
								id="state"
								required
								defaultValue={state.address.state}
								onChange={e => setState({ ...state, address: { ...state.address, state: e.target.value }})}
								onBlur={e => setState({ ...state, address: { ...state.address, state: e.target.value }})}
								autoComplete="address-level1"
							>
								<option value="">Select a State</option>
								<option value="Alabama">Alabama</option>
								<option value="Alaska">Alaska</option>
								<option value="Arizona">Arizona</option>
								<option value="Arkansas">Arkansas</option>
								<option value="California">California</option>
								<option value="Colorado">Colorado</option>
								<option value="Connecticut">Connecticut</option>
								<option value="Delaware">Delaware</option>
								<option value="District Of Columbia">District Of Columbia</option>
								<option value="Florida">Florida</option>
								<option value="Georgia">Georgia</option>
								<option value="Hawaii">Hawaii</option>
								<option value="Idaho">Idaho</option>
								<option value="Illinois">Illinois</option>
								<option value="Indiana">Indiana</option>
								<option value="Iowa">Iowa</option>
								<option value="Kansas">Kansas</option>
								<option value="Kentucky">Kentucky</option>
								<option value="Louisiana">Louisiana</option>
								<option value="Maine">Maine</option>
								<option value="Maryland">Maryland</option>
								<option value="Massachusetts">Massachusetts</option>
								<option value="Michigan">Michigan</option>
								<option value="Minnesota">Minnesota</option>
								<option value="Mississippi">Mississippi</option>
								<option value="Missouri">Missouri</option>
								<option value="Montana">Montana</option>
								<option value="Nebraska">Nebraska</option>
								<option value="Nevada">Nevada</option>
								<option value="New Hampshire">New Hampshire</option>
								<option value="New Jersey">New Jersey</option>
								<option value="New Mexico">New Mexico</option>
								<option value="New York">New York</option>
								<option value="North Carolina">North Carolina</option>
								<option value="North Dakota">North Dakota</option>
								<option value="Ohio">Ohio</option>
								<option value="Oklahoma">Oklahoma</option>
								<option value="Oregon">Oregon</option>
								<option value="Pennsylvania">Pennsylvania</option>
								<option value="Puerto Rico">Puerto Rico</option>
								<option value="Rhode Island">Rhode Island</option>
								<option value="South Carolina">South Carolina</option>
								<option value="South Dakota">South Dakota</option>
								<option value="Tennessee">Tennessee</option>
								<option value="Texas">Texas</option>
								<option value="Utah">Utah</option>
								<option value="Vermont">Vermont</option>
								<option value="Virginia">Virginia</option>
								<option value="Washington">Washington</option>
								<option value="West Virginia">West Virginia</option>
								<option value="Wisconsin">Wisconsin</option>
								<option value="Wyoming">Wyoming</option>
							</select>
						</div>

						<FormInput
							className="col-span-6 md:col-span-2"
							label="Zip Code"
							type="text"
							required
							value={state.address.zip}
							onChange={e => setState({ ...state, address: { ...state.address, zip: e.target.value }})}
							autoComplete="postal-code"
						/>

						<Switch.Group>
							<div className="flex items-center justify-center space-x-4 col-span-6">
								<Switch.Label className="text-sm sm:text-base md:text-lg">Subscribe to our newsletter</Switch.Label>
								<Switch checked={state.newsletter} onChange={() => setState({ ...state, newsletter: !state.newsletter })} className={`${state.newsletter ? 'bg-green-400' : 'bg-gray-400'} relative flex items-center px-0.5 shrink-0 h-5 w-9 rounded-full cursor-pointer transition-colors duration-200`}>
									<span className="sr-only">Use setting</span>
									<span aria-hidden="true" className={`${state.newsletter ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transform transition-transform duration-200`} />
								</Switch>
							</div>
						</Switch.Group>
					</div>

					{reasons.length > 0 && (
						<div className="text-center text-red-700 mt-6 space-y-3 md:mt-8 md:space-y-6">
							{reasons}
						</div>
					)}

					{status === Status.Error && (
						<p className="mt-6 text-red-700 font-bold text-center">An unexpected error has occured. Please try again later.</p>
					)}

					<button className="block mx-auto mt-6 py-2 w-full bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase text-base font-extrabold leading-5 md:mt-8 md:h-11 md:max-w-md md:text-lg" type="submit">{status === Status.Loading ? 'Registering' : 'Register'}</button>

					{ isDealer !== true && (
						<div className="mt-6 text-center space-y-3 md:mt-12">
							<p className="text-sm md:text-base">Already have an account? <Link href={loginUrl}><a className="underline text-green-400 text-sm font-semibold md:text-base">Log in</a></Link></p>

							<Link href="/"><a className="inline-block underline text-gray-800 text-sm font-semibold md:text-base">Return to home</a></Link>
						</div>
					)}
				</>
			)}
		</form>
	)
}
